import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Radio } from 'antd';
import { Button, Flex, InfoTooltip } from 'ui';
import moment from 'utils/cops-moment';
import styled from 'styled-components';
import RangeFilter from 'common/filters/range-filter';
import StepRangeFilter from 'common/filters/step-range-filter';
import { injectIntl } from 'react-intl';

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
  overflow: hidden;
  > :first-child {
    margin-top: 10px;
  }
`;

const StyledSlider = styled(Flex)`
  margin-top: 30px;
  width: 100%;
  ${p => p.isColumnFilter ? `
    flex-flow: column nowrap;
  ` : `
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: flex-start;
  `}
`;

const StyledRadio = styled.div`
  margin-top: 5px;
  width: ${p => p.isColumnFilter ? '350px' : '20%'};
  ${p => p.isColumnFilter && 'margin-bottom: 10px;'}
`;

const Wrapper = styled.div`
  width: ${p => p.isColumnFilter ? '475px' : '80%'};
`;

const SubTitle = styled.div`
  font-size: 16px;
  display: inline-block;
  margin-right: 10px;
`;

const Title = styled.span`
  font-weight: 400;
  font-size: 16px;
  display: inline-block;
  margin-right: 10px;
`;

const ClearButton = styled.span`
  color: ${p => p.theme.colors.theme};
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
`;

const ConfirmButton = styled(Button)`
  text-transform: uppercase;
`;

const CombinedRange = ({
  filters,
  activeFilters,
  onChange,
  intl,
  isColumnFilter,
  disabled,
  needsConfirm = false,
  info = '',
}) => {
  const { id } = filters;
  const [orderDateSelectedRange, setOrderDateSelectedRange] = useState('relative');
  const [currentVal, setCurrentVal] = useState({
    relative: ['MinusInfinity', 'PlusInfinity'],
    absolute: [filters.min, filters.max],
  });

  const onRangeFilterChange = (values) => {
    setCurrentVal(
      { ...currentVal, [orderDateSelectedRange]: values },
    );
    onChange(id, values);
  };

  // checks if the giving parameter is a date, if yes => absolute
  const isAbsolute = (values) => {
    if (values?.length) {
      const date = moment(values[0]);
      return date.isValid();
    }
    return false;
  };

  useEffect(() => {
    if (activeFilters[id]?.length && filters?.rangeFixPoints) {
      setOrderDateSelectedRange(isAbsolute(activeFilters[id]) ? 'absolute' : 'relative');
    }
  }, []);

  const onRangeTypeChange = (e) => {
    if (activeFilters[id]?.length) {
      onChange(id, currentVal[e.target.value]);
    }
    setOrderDateSelectedRange(e.target.value);
  };

  const handleClear = () => {
    onRangeFilterChange([]);
    setOrderDateSelectedRange('relative');
  };

  const showClearButton = () => <>
    {'( '}
    <ClearButton onClick={handleClear}>
      {intl.formatMessage({ id: 'worklist.filters.CLEAR' })}
    </ClearButton>
    {' )'}
  </>;

  const renderRadiobox = (filterDiscription, orderValue) =>
    <StyledRadio isColumnFilter={isColumnFilter}>
      <Radio value={orderValue}>
        <SubTitle>{filterDiscription}</SubTitle>
        {isColumnFilter ? showClearButton() : null}
      </Radio>
    </StyledRadio>;

  return (
    <>
      {!isColumnFilter ?
        <>
          <Title>
            {filters.description.toUpperCase()}
            <InfoTooltip content={info} />
          </Title>
          {showClearButton()}
        </>
        : null
      }
      <StyledRadioGroup
        onChange={onRangeTypeChange}
        value={orderDateSelectedRange}
      >
        <>
          <StyledSlider isColumnFilter={isColumnFilter}>
            {renderRadiobox(filters.fixPointRangeDescription, 'relative')}
            <Wrapper isColumnFilter={isColumnFilter}>
              <StepRangeFilter
                description={''}
                relativeTimingRangeOptions={filters?.rangeFixPoints}
                min='MinusInfinity'
                max='PlusInfinity'
                unit={filters.unit}
                activeFilters={!isAbsolute(activeFilters[id]) ?
                  activeFilters[id] : currentVal.relative}
                onChange={values =>
                  onRangeFilterChange(values)
                }
                inActive={orderDateSelectedRange !== 'relative'}
                disabled={disabled}
                allowClear={false}
                isColumnFilter={isColumnFilter}
              />
            </Wrapper>
          </StyledSlider>
          <StyledSlider isColumnFilter={isColumnFilter}>
            {renderRadiobox(filters.absoluteRangeDescription, 'absolute')}
            <Wrapper isColumnFilter={isColumnFilter}>
              <RangeFilter
                description={''}
                min={filters.min}
                max={filters.max}
                unit={filters.unit}
                activeFilters={isAbsolute(activeFilters[id]) ?
                  activeFilters[id] : currentVal.absolute}
                onChange={values =>
                  onRangeFilterChange(values)
                }
                inActive={orderDateSelectedRange !== 'absolute'}
                disabled={disabled}
                allowClear={false}
                isColumnFilter={isColumnFilter}
              />
            </Wrapper>
          </StyledSlider>
        </>,
      </StyledRadioGroup>
      {needsConfirm && <ConfirmButton
        type='secondary'
        disabled={!currentVal?.length}
        onClick={() => onRangeFilterChange(currentVal)}
      >
        {intl.formatMessage({ id: 'worklist.filters.APPLY' })}
      </ConfirmButton>
      }
    </>
  );
};
export default injectIntl(CombinedRange);

CombinedRange.propTypes = {
  isColumnFilter: PropTypes.bool,
  filters: PropTypes.object,
  intl: PropTypes.object,
  needsConfirm: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  activeFilters: PropTypes.object,
  disabled: PropTypes.bool,
  info: PropTypes.string,
};

